<template>
  <div class="calendar">
    <h3>Company Events</h3>
    <hr class="divider">
    <div class="list-container">
      <div v-if="loading" class="calendar-loading">Loading events...</div>
      <div v-else-if="error" class="calendar-error">{{ error }}</div>
      <div v-else>
        <div v-if="events.length === 0" class="calendar-no-events">
          No upcoming events in the next 3 months.
        </div>
        <ul v-else class="calendar-events-list">
          <li v-for="event in events" :key="event.id" class="calendar-event-item">
            <a
              :href="`/#/event/${event.slug}`"
              class="event-link"
              style="display: flex; align-items: flex-start; gap: 1rem; width: 100%; text-decoration: none;"
            >
              <div class="event-logo">
                <i :class="getEventIcon(event)" aria-hidden="true"></i>
              </div>
              <div class="event-details">
                <div class="event-title">
                  {{ event.title?.rendered || event.title || 'Untitled Event' }}
                </div>
                <p class="event-date-text">
                  {{ formatShortDateNoYear(event.event_date) }}
                  <template v-if="showEndDate(event)">
                    , {{ formatShortDateNoYear(event.acf?.end_date) }}
                  </template>
                </p>
                <div class="event-meta">
                  <span v-if="event.event_time">{{ event.event_time }}</span>
                  <span v-if="event.event_location"> | {{ event.event_location }}</span>
                </div>
              </div>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchCurrentUser, fetchCategoriesBySlugs, safeFetch,category_from_role } from '@/utils/fetch.js';

const CACHE_KEY = 'events_cache';
const CACHE_EXPIRY = 60 * 60 * 1000; // 1 hour in milliseconds

function isWithinNext3Months(dateStr) {
  if (!dateStr) return false;
  const now = new Date();
  const eventDate = new Date(dateStr);
  const threeMonthsLater = new Date(now);
  threeMonthsLater.setMonth(now.getMonth() + 3);
  return eventDate >= now && eventDate <= threeMonthsLater;
}

export default {
  name: "calendar",
  data() {
    return {
      events: [],
      loading: true,
      error: null,
      eventIcons: {
        birthday: ['fa-light', 'fa-cake-candles'],
        time_off: ['fa-light', 'fa-calendar-xmark'],
        default: ['fa-light', 'fa-calendar-range']
      },
      userCategoryIds: [],
    };
  },
  methods: {
    async fetchEvents() {
      this.loading = true;
      this.error = null;
      
      try {
        // Get user and allowed category IDs
        const user = await fetchCurrentUser();
        const roles = user.my_roles || [];
        const roleCategorySlugs = roles.map(role => category_from_role[role]).filter(Boolean);
        let allowedCategories = [];
        if (roleCategorySlugs.length) {
          const categories = await fetchCategoriesBySlugs(roleCategorySlugs);
          allowedCategories = categories.map(cat => cat.id);
        }
        this.userCategoryIds = allowedCategories;

        // Check for cached data first
        const cachedData = this.getCachedEvents();
        if (cachedData) {
          // Filter cached events: include if no categories, or if matches allowed categories
          this.events = cachedData.filter(event =>
            !event.categories || event.categories.length === 0 ||
            event.categories.some(catId => this.userCategoryIds.includes(catId))
          );
          this.loading = false;
          return;
        }
        
        // If no cache or expired, fetch from API
        const res = await safeFetch(
          `/wp-json/wp/v2/events?per_page=50&_embed`
        );
        if (!res.ok) throw new Error("Failed to fetch events");
        let data = await res.json();
        data = data
          .map((event) => {
            let rawDate = event.acf?.date || null;
            let event_date = null;
            if (rawDate && /^\d{8}$/.test(rawDate)) {
              event_date = `${rawDate.slice(0,4)}-${rawDate.slice(4,6)}-${rawDate.slice(6,8)}`;
            }
            let rawEndDate = event.acf?.end_date || null;
            let end_date = null;
            if (rawEndDate && /^\d{8}$/.test(rawEndDate)) {
              end_date = `${rawEndDate.slice(0,4)}-${rawEndDate.slice(4,6)}-${rawEndDate.slice(6,8)}`;
            }
            return {
              ...event,
              event_date,
              acf: {
                ...event.acf,
                end_date
              }
            };
          })
          // Filter by allowed categories or no categories (General)
          .filter(event =>
            !event.categories || event.categories.length === 0 ||
            event.categories.some(catId => this.userCategoryIds.includes(catId))
          )
          .filter((event) => isWithinNext3Months(event.event_date))
          .sort(
            (a, b) =>
              new Date(a.event_date).getTime() - new Date(b.event_date).getTime()
          );

        // Cache the processed data
        this.cacheEvents(data);
        
        this.events = data;
      } catch (err) {
        this.error = err.message;
      } finally {
        this.loading = false;
      }
    },
    
    // Cache the events data in localStorage
    cacheEvents(eventsData) {
      try {
        const cacheData = {
          timestamp: new Date().getTime(),
          events: eventsData
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      } catch (err) {
        console.warn("Failed to cache events:", err);
      }
    },
    
    // Get cached events if available and not expired
    getCachedEvents() {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) return null;
        
        const cacheData = JSON.parse(cached);
        const now = new Date().getTime();
        
        // Check if cache is expired
        if (now - cacheData.timestamp > CACHE_EXPIRY) {
          console.log("Cache expired");
          localStorage.removeItem(CACHE_KEY);
          return null;
        }
        
        return cacheData.events;
      } catch (err) {
        console.warn("Error reading cache:", err);
        return null;
      }
    },
    formatDate(dateStr, part = "full") {
      if (!dateStr) return "";
      // Add a time to prevent timezone offset issues
      const date = new Date(dateStr + "T12:00:00");
      if (part === "day") return date.getDate();
      if (part === "month")
        return date.toLocaleString("default", { month: "short" });
      return date.toLocaleDateString();
    },
    formatFullDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr + "T12:00:00");
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    formatShortDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr + "T12:00:00");
      return date.toLocaleDateString('en-US', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
      });
    },
    formatShortDateNoYear(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr + "T12:00:00");
      const now = new Date();
      if (date.getFullYear() !== now.getFullYear()) {
        return date.toLocaleDateString('en-US', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit'
        });
      }
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit'
      });
    },
    truncateContent(html, maxLength = 120) {
      // Remove HTML tags and trim to maxLength
      const text = html.replace(/<[^>]+>/g, '').trim();
      return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
    },
    getEventType(event) {
      // Determine event type from categories, tags, or other fields
      if (event.acf?.event_type == 'birthday') return 'birthday';
      if (event.acf?.event_type == 'time-off') return 'time_off';

      // Fallback: check for keywords in title
      const title = event.title?.rendered || event.title || '';
      if (title.toLowerCase().includes('birthday')) return 'birthday';
      if (title.toLowerCase().includes('time off')) return 'time_off';
      if (title.toLowerCase().includes('day off')) return 'time_off';
      
      return 'default';
    },
    getEventIcon(event) {
      const eventType = this.getEventType(event);
      const iconClasses = this.eventIcons[eventType] || this.eventIcons.default;
      return ['fa', ...iconClasses];
    },
    showEndDate(event) {
      const start = event.event_date;
      const end = event.acf?.end_date;
      if (!end) {return false;}

      // Compare as dates
      const startDate = new Date(start + "T12:00:00");
      const endDate = new Date(end + "T12:00:00");
      return endDate > startDate;
    }
  },
  mounted() {
    this.fetchEvents();
  },
};
</script>

<style scoped>
h3 {
  text-align: left;
  font-size: 20px;
  font-weight: 325;
}
.calendar, .list-container, .calendar-events-list, .calendar-event-item, .event-logo, .event-details {
  box-sizing: border-box;
}
.calendar {
  border-radius: 8px;
  padding: 1.5rem;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.list-container {
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  border-radius: 8px;
}
.calendar-events-list {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1 1 0;
  overflow-y: auto;
  min-height: 0;
}
.calendar-event-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: left;
}
.event-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: .15em;
  min-width: 48px;
  font-size: 38px; /* Size for the FontAwesome icon */
  color: #0974b2; /* Color for the icon */
}
.event-logo i {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.event-details {
  flex: 1;
}
.event-title {
  font-weight: 500;
  color: #666;
  text-decoration: none;
  font-size: 1.1em;
}
.event-date-text {
  font-size: 0.97em;
  color: #666;
  margin: 0.2em 0 0.5em 0;
  line-height: 1.3;
}
.event-meta {
  font-size: 0.95em;
  color: #888;
  margin-bottom: 0.3em;
}
.event-excerpt {
  font-size: 0.98em;
  color: #555;
}
.calendar-loading,
.calendar-error,
.calendar-no-events {
  color: #888;
  font-size: 1em;
  margin: 1em 0;
}
hr.divider {
  border: 0;
  border-top: 1px solid #D5D1C8;
  margin: 0 0 10px;
}
.event-link:hover {
  background: #f5f8fa;
  border-radius: 8px;
  text-decoration: none;
}
</style>